import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { SalesService } from '../../../../shared/services/apis/platform/sales.service';
import { AdminSaleDetailsResponse } from '../../../../shared/services/responses/platform/admin-sales.response';
import { CORREIOS_SERVICES, TRACKING_CODE_REGEX } from '../sales-status';
import { UtcDatePipe } from '../utc-date.pipe';

export interface LabelUploadDialogData {
  details: AdminSaleDetailsResponse;
}

/**
 * Etiqueta gerada fora do fluxo automático (portal dos Correios etc.).
 * Mostra os dados que a etiqueta precisa conter (os mesmos da pré-postagem montada pelo worker) para conferência.
 */
@Component({
  selector: 'app-label-upload-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatProgressSpinnerModule, UtcDatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h2 mat-dialog-title class="title">{{ hasLabel ? 'Substituir por etiqueta manual' : 'Enviar etiqueta manual' }} <span class="muted">· venda #{{ d.sale.idKitPayment }}</span></h2>
    <mat-dialog-content class="content">
      <p class="intro">Use quando a etiqueta foi gerada fora do fluxo automático. O worker deixa de gerar a pré-postagem para esta venda.</p>

      <div class="drop" [class.has]="file" (click)="fileInput.click()" (dragover)="$event.preventDefault()" (drop)="onDrop($event)" tabindex="0" (keydown.enter)="fileInput.click()">
        <mat-icon>label</mat-icon>
        <b>Etiqueta (.pdf)</b>
        <span *ngIf="file">{{ file.name }} ({{ (file.size / 1024).toFixed(0) }} KB)</span>
        <span *ngIf="!file">Arraste ou clique para escolher</span>
        <input #fileInput type="file" accept="application/pdf,.pdf" hidden (change)="onPick($event)">
      </div>

      <div class="form">
        <mat-form-field appearance="outline">
          <mat-label>Código de rastreio *</mat-label>
          <input matInput [(ngModel)]="trackingCode" (ngModelChange)="trackingCode = $event.toUpperCase()" placeholder="AA123456789BR" maxlength="13" class="mono">
          <mat-hint>Formato AA123456789BR</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Serviço *</mat-label>
          <mat-select [(ngModel)]="serviceCode">
            <mat-option *ngFor="let s of services" [value]="s.code">{{ s.name }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline" class="full">
          <mat-label>Nº da pré-postagem (opcional)</mat-label>
          <input matInput [(ngModel)]="prePostagemId" placeholder="Se vazio grava MANUAL-{{ d.sale.idKitPayment }}">
        </mat-form-field>
      </div>

      <div class="ro-grid">
        <div class="ro">
          <h4>Remetente (gráfica)</h4>
          <ng-container *ngIf="d.delivery.printCompany as pc; else noPc">
            {{ pc.name }}<span *ngIf="pc.cpfCnpj"> · {{ pc.cpfCnpj }}</span><br>
            {{ pc.street }}, {{ pc.number }}<span *ngIf="pc.complement"> · {{ pc.complement }}</span> · {{ pc.neighborhood }} · {{ pc.city }}/{{ pc.state }} · {{ pc.zipcode }}
          </ng-container>
          <ng-template #noPc>—</ng-template>
        </div>
        <div class="ro">
          <h4>Destinatário</h4>
          {{ d.customer.name }}<span *ngIf="d.customer.cpf"> · CPF {{ d.customer.cpf }}</span><br>
          {{ d.customer.street }}, {{ d.customer.number }}<span *ngIf="d.customer.complement"> · {{ d.customer.complement }}</span>
          <span *ngIf="d.customer.neighborhood"> · {{ d.customer.neighborhood }}</span> · {{ d.customer.city }}/{{ d.customer.state }} · {{ d.customer.zipcode }}
        </div>
        <div class="ro">
          <h4>NF-e</h4>
          <ng-container *ngIf="d.fiscal.nfeNumber; else noNfe">
            nº {{ d.fiscal.nfeNumber }} · gerada em {{ d.fiscal.datetimeNfeGenerated | utcDate }}<br>
            <span class="mono xs">{{ d.fiscal.nfeKey }}</span>
          </ng-container>
          <ng-template #noNfe><span class="warn">NF-e pendente: anexe antes da etiqueta</span></ng-template>
        </div>
        <div class="ro">
          <h4>Pacote (calculado)</h4>
          <ng-container *ngIf="d.delivery.package as p; else noPkg">
            {{ p.weightG }} g<span *ngIf="p.formato === '2'"> · {{ p.heightCm }}×{{ p.widthCm }}×{{ p.lengthCm }} cm</span><span *ngIf="p.formato === '1'"> · envelope</span> · NCM {{ p.ncm }}<br>
            Declaração: <span *ngFor="let i of p.declaration; let last = last">{{ i.content }} ×{{ i.quantity }}<span *ngIf="!last">; </span></span>
          </ng-container>
          <ng-template #noPkg>—</ng-template>
        </div>
      </div>

      <div class="note">
        <mat-icon>info</mat-icon>
        <span>Confira se a etiqueta traz remetente, destinatário, serviço, NF-e e código de rastreio idênticos aos dados acima. A venda passa para <b>Aguardando produção</b> e aparece na página da gráfica.</span>
      </div>
      <div class="note warn" *ngIf="hasLabel">
        <mat-icon>warning</mat-icon>
        <span>Esta venda já tem etiqueta{{ d.delivery.isManualLabel ? ' manual' : ' gerada pelos Correios' }}. A nova etiqueta e o novo código de rastreio substituem os atuais.</span>
      </div>
      <div class="error" *ngIf="error">{{ error }}</div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-stroked-button (click)="close()" [disabled]="sending">Cancelar</button>
      <button mat-flat-button class="btn-primary" (click)="send()" [disabled]="!canSend || sending">
        <mat-spinner *ngIf="sending" diameter="18"></mat-spinner>
        <ng-container *ngIf="!sending"><mat-icon>upload</mat-icon> Salvar etiqueta</ng-container>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .title { font-weight: 800; font-size: 1.15rem; }
    .muted { color: #6b7280; font-weight: 400; font-size: .95rem; }
    .content { max-width: 760px; }
    .intro { color: #4b5563; margin: 0 0 14px; }
    .drop { border: 2px dashed #cfd6d1; border-radius: 12px; padding: 16px 12px; text-align: center; cursor: pointer; background: #fafbf9; display: flex; flex-direction: column; align-items: center; gap: 4px; color: #4b5563; font-size: .9rem; outline: none; margin-bottom: 14px; }
    .drop:hover, .drop:focus-visible { border-color: #5daf50; background: #e9f5e6; }
    .drop mat-icon { font-size: 30px; width: 30px; height: 30px; color: #3d8b32; }
    .drop b { color: #1f2a24; }
    .drop.has { border-style: solid; border-color: #5daf50; background: #e9f5e6; }
    .form { display: grid; grid-template-columns: 1fr 1fr; gap: 0 14px; }
    .form .full { grid-column: 1 / -1; }
    @media (max-width: 600px) { .form { grid-template-columns: 1fr; } }
    .mono { font-family: ui-monospace, Consolas, monospace; }
    .xs { font-size: .78rem; word-break: break-all; }
    .ro-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 4px; }
    @media (max-width: 600px) { .ro-grid { grid-template-columns: 1fr; } }
    .ro { background: #fafbf9; border: 1px solid #e3e7e3; border-radius: 8px; padding: 10px 12px; font-size: .85rem; color: #1f2a24; }
    .ro h4 { margin: 0 0 6px; font-size: .72rem; text-transform: uppercase; letter-spacing: .07em; color: #8a958e; }
    .warn { color: #b26a00; font-weight: 600; }
    .note { display: flex; gap: 8px; align-items: flex-start; border-radius: 8px; padding: 10px 12px; font-size: .85rem; margin-top: 12px; background: #e7f0fb; color: #1565c0; }
    .note.warn { background: #fff3e0; color: #b26a00; }
    .note mat-icon { font-size: 18px; width: 18px; height: 18px; flex: none; }
    .error { color: #c62828; margin-top: 10px; font-size: .9rem; }
    mat-dialog-actions button mat-spinner { display: inline-block; margin-right: 6px; }
  `]
})
export class LabelUploadDialogComponent {
  readonly services = CORREIOS_SERVICES;
  d: AdminSaleDetailsResponse;
  file: File | null = null;
  trackingCode = '';
  serviceCode: string;
  prePostagemId = '';
  sending = false;
  error = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: LabelUploadDialogData,
    private dialogRef: MatDialogRef<LabelUploadDialogComponent, boolean>,
    private sales: SalesService,
    private alert: AlertService,
    private cdr: ChangeDetectorRef,
  ) {
    this.d = data.details;
    const current = this.d.delivery.deliveryType ?? '03298';
    this.serviceCode = this.services.some(s => s.code === current) ? current : (current === '20117' ? '20192' : '03298');
  }

  get hasLabel(): boolean { return !!this.d.delivery.linkCorreiosCartaRemessa; }

  get canSend(): boolean {
    return !!this.file && TRACKING_CODE_REGEX.test((this.trackingCode || '').trim()) && !!this.serviceCode && !!this.d.fiscal.nfeNumber;
  }

  onPick(ev: Event) {
    const input = ev.target as HTMLInputElement;
    this.setFile(input.files?.[0] ?? null);
    input.value = '';
  }

  onDrop(ev: DragEvent) {
    ev.preventDefault();
    this.setFile(ev.dataTransfer?.files?.[0] ?? null);
  }

  private setFile(file: File | null) {
    this.error = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { this.error = 'A etiqueta deve ser um arquivo .pdf.'; this.cdr.markForCheck(); return; }
    this.file = file;
    this.cdr.markForCheck();
  }

  send() {
    if (!this.canSend || !this.file) return;
    this.sending = true;
    this.error = '';
    this.sales.uploadLabel(this.d.sale.idKitPayment, this.file, {
      trackingCode: this.trackingCode.trim().toUpperCase(),
      serviceCode: this.serviceCode,
      prePostagemId: this.prePostagemId.trim() || null,
      replace: this.hasLabel,
    }).subscribe({
      next: (res) => {
        this.sending = false;
        if (res.isError) {
          this.error = res.errorDescription || 'Falha ao salvar a etiqueta.';
          this.cdr.markForCheck();
          return;
        }
        this.alert.show('Sucesso', 'Etiqueta manual salva. A venda passa para "Aguardando produção".', AlertType.success);
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.sending = false;
        this.error = err?.message || 'Erro inesperado ao salvar a etiqueta.';
        this.cdr.markForCheck();
      },
    });
  }

  close() { this.dialogRef.close(false); }
}
